import React, {
  useMemo,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableActions,
  OrderableTableRow
} from '@ubisend/pulse-components';
import { PermissionFilter } from '@ubisend/pulse-auth';

import SiteViewPanel from './SiteViewPanel';

const SiteTable = ({ data, orderProps, state }) => {
  const {
    isPanelOpen,
    selectedSite,
    setCrawlSites,
    crawlResources,
    setActiveModal,
    setIsPanelOpen,
    setCrawlFolders,
    setSelectedSite,
    setCrawlResources,
    setSelectedActionSite
  } = state;

  const sites = useMemo(() => data?.data ?? [], [data?.data]);

  const handleViewSite = useCallback((site) => {
    setSelectedSite(site);
    setIsPanelOpen(true);
    setCrawlSites(false);
    setCrawlFolders(true);
    setCrawlResources(false);
  }, [setCrawlSites, setIsPanelOpen, setCrawlFolders, setSelectedSite, setCrawlResources]);

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setCrawlSites(true);
    setCrawlFolders(false);
    setCrawlResources(false);
  }, [setCrawlSites, setIsPanelOpen, setCrawlFolders, setCrawlResources]);

  const handleDeleteSite = useCallback((site) => {
    setSelectedActionSite(site);
    setActiveModal('deleteSite');
  }, [setActiveModal, setSelectedActionSite]);

  useEffect(() => {
    if (!selectedSite) {
      return;
    }

    const stillExists = sites.some((s) => s.id === selectedSite.id);
    if (!stillExists && !(isPanelOpen && crawlResources)) {
      handleClosePanel();
    }
  }, [sites, isPanelOpen, selectedSite, crawlResources, handleClosePanel]);

  return (
    <Flex border xScroll>
      <Table>
        <TableHead>
          <OrderableTableRow
            rows={[
              { label: 'Site', sort: 'site_name' },
              { label: 'Crawled At', sort: 'last_sync_at' },
              null
            ]}
            {...orderProps}
          />
        </TableHead>

        <TableBody>
          {sites.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>No sites connected yet.</TableCell>
            </TableRow>
          ) : (
            sites.map((site) => (
              <TableRow key={site.id}>
                <TableCell>{site.site_name}</TableCell>
                <TableCell>{site.last_sync_at ?? '-'}</TableCell>
                <TableActions>
                  <Button
                    variant="secondary"
                    icon="eye"
                    colour="primary"
                    onClick={() => handleViewSite(site)}
                  >
                    View
                  </Button>
                  <PermissionFilter can="edit generative ai resource">
                    <Button
                      variant="secondary"
                      icon="trash"
                      colour="danger"
                      onClick={() => handleDeleteSite(site)}
                      disabled={site.is_deleting || data.has_pending}
                      aria-disabled={site.is_deleting || data.has_pending}
                    >
                      Delete
                    </Button>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {selectedSite && isPanelOpen && (<SiteViewPanel state={state} />)}
    </Flex>
  );
};

SiteTable.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        is_deleting: PropTypes.bool,
        last_sync_at: PropTypes.string,
        id: PropTypes.number.isRequired,
        site_name: PropTypes.string.isRequired,
      })
    ),
    has_pending: PropTypes.bool
  }).isRequired,
  orderProps: PropTypes.object.isRequired,
  state: PropTypes.shape({
    selectedSite: PropTypes.object,
    isPanelOpen: PropTypes.bool.isRequired,
    crawlFolders: PropTypes.bool.isRequired,
    refetchFolder: PropTypes.func.isRequired,
    refetchStatus: PropTypes.func.isRequired,
    setCrawlSites: PropTypes.func.isRequired,
    crawlResources: PropTypes.bool.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    setIsPanelOpen: PropTypes.func.isRequired,
    setCrawlFolders: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setCrawlResources: PropTypes.func.isRequired,
    setSelectedActionSite: PropTypes.func.isRequired
  }).isRequired
};

export default SiteTable;